import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * isLangOnlyChange — true when ONLY the language segment changed (e.g.
 * `/en/services/japantollreceipts` → `/ja/services/japantollreceipts`).
 * In that case ScrollToTop should keep the visitor exactly where they
 * were on the page instead of yanking them back to the top.
 */
function isLangOnlyChange(prev, next) {
  if (!prev || !next || prev === next) return false;
  const a = prev.split("/").filter(Boolean);
  const b = next.split("/").filter(Boolean);
  if (a.length === 0 || b.length === 0) return false;
  // Different lang prefix, same tail
  if (a[0] === b[0]) return false;
  return a.slice(1).join("/") === b.slice(1).join("/");
}

/**
 * ScrollToTop — scrolls to top on every pathname change EXCEPT when only
 * the language segment changed (per owner directive 2026-02-18: tapping
 * EN/JA mid-page must not jump back to the top).
 *
 * - Skips behavior when a hash anchor is present so #section links keep working.
 * - Respects prefers-reduced-motion (always uses `auto` which is instant).
 * - After non-lang route changes, moves keyboard focus to #main-content.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const prevPathRef = useRef(pathname);
  const prevHashRef = useRef(hash);

  useEffect(() => {
    const prevPath = prevPathRef.current;
    const prevHash = prevHashRef.current;
    prevPathRef.current = pathname;
    prevHashRef.current = hash;

    // Bug fix (Feb 2026 QA pass): `noChange` is true both on the app's
    // very first mount AND on React 18 StrictMode's dev-only synthetic
    // mount→cleanup→remount double-invoke (which reuses the same refs,
    // so pathname/hash appear unchanged on the phantom second call too).
    // We use this — rather than a simple "first render" ref — because a
    // plain ref flag gets flipped to false during the FIRST of those two
    // StrictMode invocations and is therefore already stale by the
    // second one, silently re-enabling the bug in development. Comparing
    // against the previous committed pathname/hash is robust to that
    // quirk: it only reads as "changed" on a genuine navigation.
    //
    // Why this matters: the focus-steal below used to run unconditionally
    // on mount, moving keyboard focus onto <main> before the visitor had
    // pressed Tab even once. The very first Tab press from a fresh page
    // load then jumped straight into page body content — completely
    // skipping the skip-to-content link, logo, header nav, language
    // switcher and mobile-menu toggle (all earlier in the DOM than
    // <main>) — a WCAG 2.4.3 (Focus Order) violation.
    const noChange = prevPath === pathname && prevHash === hash;

    // Lang-only switch — preserve scroll position entirely.
    if (isLangOnlyChange(prevPath, pathname)) return;

    if (hash) {
      const id = hash.replace("#", "");
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (!el) return false;
        const headerOffset = window.matchMedia("(min-width: 768px)").matches ? 72 + 64 : 64 + 56;
        const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top, behavior: "smooth" });
        return true;
      };
      if (!tryScroll()) {
        const t = setTimeout(tryScroll, 80);
        return () => clearTimeout(t);
      }
      return;
    }

    // Skip the forced scroll-to-top + focus-steal when nothing actually
    // navigated (initial page load) — the browser is already at (0,0)
    // and stealing focus here breaks the natural Tab order for
    // first-time keyboard visitors.
    if (noChange) return;

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    // Focus main content (without scrolling further) for a11y — only on
    // genuine client-side route changes, not the initial mount.
    const id = requestAnimationFrame(() => {
      const main = document.getElementById("main-content");
      if (main && typeof main.focus === "function") {
        try {
          main.focus({ preventScroll: true });
        } catch {
          main.focus();
        }
      }
    });
    return () => cancelAnimationFrame(id);
  }, [pathname, hash]);

  return null;
}
